import React from 'react';
import { Box, Typography } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import type { SocialPlatform } from '../../../types/instagramAutomation.types';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import { ASPECT_CSS, PLATFORM_LABEL, type ComposeAspect } from './composeShared';
import { StatusChip } from './manageShared';

type Props = {
    platform: SocialPlatform;
    aspect: ComposeAspect;
    isVideo: boolean;
    previews: string[];
    previewIdx: number;
    onSelectIdx: (i: number) => void;
    caption: string;
    showDots: boolean;
};

export default function ComposePhonePreview({
    platform,
    aspect,
    isVideo,
    previews,
    previewIdx,
    onSelectIdx,
    caption,
    showDots,
}: Props) {
    const src = previews[previewIdx];
    return (
        <Box
            sx={{
                width: 260,
                borderRadius: '36px',
                border: '3px solid #333',
                overflow: 'hidden',
                bgcolor: '#111',
                boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
            }}>
            <Box sx={{ height: 28, bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', px: 1.5 }}>
                <StatusChip label={PLATFORM_LABEL[platform]} tone="gold" />
            </Box>
            <Box sx={{ aspectRatio: ASPECT_CSS[aspect], bgcolor: '#222', position: 'relative' }}>
                {src ? (
                    <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: BRAND_LUXE_MUTED,
                            fontSize: 12,
                            px: 2,
                            textAlign: 'center',
                            gap: 0.5,
                        }}>
                        {isVideo ? <PlayCircleOutlineIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 36 }} /> : null}
                        {isVideo ? 'Video — pick a project or add a cover' : 'Preview'}
                    </Box>
                )}
                {isVideo && src ? (
                    <PlayCircleOutlineIcon
                        sx={{ position: 'absolute', right: 8, bottom: 8, color: '#fff', fontSize: 28 }}
                    />
                ) : null}
            </Box>
            <Box sx={{ p: 1.5 }}>
                <Typography
                    sx={{
                        color: '#fff',
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        whiteSpace: 'pre-wrap',
                        minHeight: 36,
                    }}>
                    {caption || 'Caption preview'}
                </Typography>
                {showDots ? (
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 1 }}>
                        {previews.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => onSelectIdx(i)}
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: i === previewIdx ? BRAND_LUXE_PRIMARY : '#555',
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}
